import { ChangeDetectionStrategy, Component, HostBinding, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { AccountAppValue } from "@fabric-types/app-account";
import { Store } from "@ngrx/store";
import { OmsMessage, OmsMessageStatus } from "@obg/ngx.common";
import { CORE_ROUTE, CoreState } from "@obg/ngx.core";
import { DocumentRef } from "@obg/ngx.global.evo";
import { RouteAction } from "@obg/ngx.router";
import { partition } from "lodash-es";
import { Observable, Subject, Subscription } from "rxjs";
import { map, shareReplay, take, tap } from "rxjs/operators";

import { MAT_TOOLTIP_CRS_KEY, MAX_MESSAGES_TO_SHOW, OMS_TRACKING_DATA } from "./oms-summary.const";

@Component({
	selector: "obg-app-m-oms-summary-container",
	templateUrl: "./oms-summary.container.html",
	styleUrls: ["./oms-summary.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class OmsSummaryContainer implements OnInit, OnDestroy {
	messages$: Observable<OmsMessage[] | undefined> | undefined;
	showMoreCount = 0;
	unreadMessagesCount$: Observable<number> | undefined;
	isBusy$: Observable<boolean> | undefined;
	clickStream$ = new Subject<Event>();
	omsTrackingData = OMS_TRACKING_DATA;
	matTooltipCrsKey = MAT_TOOLTIP_CRS_KEY;

	@HostBinding("class.obg-m-oms-summary") class = true;

	private data$$: Subscription | undefined;
	private lastUnreadMessagesCount = 0;

	constructor(
		private store: Store<CoreState>,
		private documentRef: DocumentRef
	) {}

	ngOnInit() {
		this.loadUnreadMessagesCount();

		this.isBusy$ = new Observable(this.documentRef.obgEventBus.retrieve(AccountAppValue.GetOmsIsBusy));

		const clickHandler$ = this.clickStream$.pipe(
			take(1),
			tap(() => {
				if (this.messages$) {
					return;
				}
				this.loadActiveMessages();
			})
		);

		this.data$$ = clickHandler$.subscribe();
	}

	loadUnreadMessagesCount() {
		this.unreadMessagesCount$ = new Observable(
			this.documentRef.obgEventBus.retrieve(AccountAppValue.PollNotificationsInfo)
		).pipe(
			map(({ data }) => {
				const unreadMessagesCount = data?.unreadNotificationCount || 0;
				if (this.lastUnreadMessagesCount !== unreadMessagesCount) {
					this.loadActiveMessages();
				}
				this.showMoreCount = Math.max(unreadMessagesCount - MAX_MESSAGES_TO_SHOW, 0);
				return unreadMessagesCount;
			}),
			tap((unreadMessagesCount) => (this.lastUnreadMessagesCount = unreadMessagesCount)),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	loadActiveMessages() {
		this.messages$ = new Observable(
			this.documentRef.obgEventBus.retrieve(AccountAppValue.GetNotificationsByStatus, {
				status: OmsMessageStatus.Active,
			})
		).pipe(
			map((messages) => {
				const [unread, read] = partition(messages, (message) => !message.isRead);
				return [...unread.slice(0, MAX_MESSAGES_TO_SHOW), ...read].slice(0, MAX_MESSAGES_TO_SHOW);
			})
		);
	}

	ngOnDestroy() {
		if (this.data$$) {
			this.data$$.unsubscribe();
		}
	}

	goToOmsCenter(messageId?: number | undefined) {
		this.store.dispatch(
			RouteAction.navigate(CORE_ROUTE.oms.messages, undefined, { queryParams: { id: messageId } })
		);
	}
}
